/**
 * Shared Tank level Attribute Configuration
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { ActivityContext, AttributeConfig } from "../../types"
import { convertToH2Weight } from "@hydrologiq/shared/utils/deprecated/hydrogenCalc"
import { fillLevelIoTSource } from "../iot-sources"
import { getAsset } from "./asset"

export { validatePercentage as validateFillLevel } from "../validators"

/**
 * Universal calculator for tank level from pressure, temperature, and volume
 * Works for all activity types and statuses
 */
export const fillLevelCalculator = async (ctx: ActivityContext) => {
  const pressure = parseFloat(
    ctx.currentValues?.pressure ||
    ctx.activity.attributes?.find(
      (a: gqlTypes.ActivityAttribute) =>
        a.attribute === gqlTypes.ActivityAttributeKind.Pressure && a.event_kind === ctx.eventKind
    )?.value ||
    ctx.activity.attributes?.find(
      (a: gqlTypes.ActivityAttribute) => a.attribute === gqlTypes.ActivityAttributeKind.Pressure
    )?.value ||
    ""
  )
  const temperature = parseFloat(
    ctx.currentValues?.temperature ||
    ctx.activity.attributes?.find(
      (a: gqlTypes.ActivityAttribute) =>
        a.attribute === gqlTypes.ActivityAttributeKind.Temperature && a.event_kind === ctx.eventKind
    )?.value ||
    ctx.activity.attributes?.find(
      (a: gqlTypes.ActivityAttribute) => a.attribute === gqlTypes.ActivityAttributeKind.Temperature
    )?.value ||
    ""
  )
  const asset = getAsset(ctx)
  // Get asset from order (assets are now assigned at order level)
  const volume = asset?.volume ?? null

  if (isNaN(pressure) || isNaN(temperature) || !volume) {
    return null
  }

  const result = convertToH2Weight(pressure, temperature, volume).toString()
  return result
}

export const baseFillLevelConfig: Partial<AttributeConfig> = {
  attribute: gqlTypes.ActivityAttributeKind.FillLevel,
  label: "Tank Level (kg)",
  validator: (value) => {
    // No restrictions - allow any positive number
    if (!value) return { valid: true }
    const num = parseFloat(value)
    if (isNaN(num)) {
      return { valid: false, error: "Must be a valid number" }
    }
    if (num < 0) {
      return { valid: false, error: "Must be positive" }
    }
    return { valid: true }
  },
  order: 20, // Default order
  calculator: fillLevelCalculator, // Always calculate from P, T, V
  calculatorDependencies: [
    gqlTypes.ActivityAttributeKind.Pressure,
    gqlTypes.ActivityAttributeKind.Temperature,
  ],
  iotSource: fillLevelIoTSource(),
}

/**
 * Tank level for Started status
 * User manually records initial tank level
 */
export const fillLevelForStarted = (editable: boolean = true, required: boolean = false): AttributeConfig =>
({
  ...baseFillLevelConfig,
  visibility: "iot",
  editable: editable,
  required: required,
  helpText: `Tank level at start (auto-calculated from pressure × temperature × asset volume)`,
} as AttributeConfig)

/**
 * Tank level for Completed status with IoT support
 * Auto-populated from sensor, can be calculated from pressure/temperature/volume
 */
export const fillLevelForCompleted = (
  activityKind: string,
  calculator?: (ctx: ActivityContext) => Promise<string | null>
): AttributeConfig =>
({
  ...baseFillLevelConfig,
  visibility: "iot",
  editable: true,
  required: false,
  helpText: `Final ${activityKind} hydrogen level (from sensor or calculated)`,
  calculator,
} as AttributeConfig)

/**
 * Hidden tank level (for Scheduled status)
 */
export const fillLevelHidden: AttributeConfig = {
  ...baseFillLevelConfig,
  visibility: "hidden",
  editable: false,
  required: false,
} as AttributeConfig
